/**
 * Pure license-info helpers shared by as-components and as-home.
 * No DOM access — consumers render the results themselves.
 */

export interface LicenseData {
  type: string | null;
  /** Number of licensed seats; 0 = no restriction */
  seats?: number;
  startDate: string | null;
  endDate: string | null;
  applications?: string[];
}

export interface LicenseInfo {
  hasLicense: boolean;
  /** Normalized lowercase type, e.g. 'trial' | 'paid' */
  type: string | null;
  isTrial: boolean;
  /** Whole days until endDate (ceil); null when endDate missing/unparseable */
  daysRemaining: number | null;
  /** null = not provided; 0 = unlimited */
  seats: number | null;
  /** Parsed license period bounds; null when missing/unparseable */
  startDate: Date | null;
  endDate: Date | null;
  /** Localized period label, e.g. "20/10/2025 – 30/08/2025" or "until 30/08/2025"; '' when no end date */
  validityLabel: string;
  /** Ready-to-show fallback label, e.g. "Trial – 12 days left" */
  label: string;
  /** Dict key matching the type: license.trial / license.paid / license.none */
  dictKey: string;
  /** True when there is no license (render in a warning color) */
  isWarning: boolean;
}

/**
 * Tolerant date parser for license date strings. Accepts ISO strings,
 * server-style "2025-07-15 00:00:00", and pure-digit epoch-seconds strings
 * (dummy data uses "0" / "999999999").
 */
export function parseLicenseDate(value: string | null | undefined): Date | null {
  if (value == null) return null;
  const str = String(value).trim();
  if (!str) return null;

  if (/^\d+$/.test(str)) {
    const date = new Date(parseInt(str, 10) * 1000);
    return isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(str.includes(' ') && !str.includes('T') ? str.replace(' ', 'T') : str);
  return isNaN(date.getTime()) ? null : date;
}

/** Whole days (ceil) until the license end date; null when unavailable. */
export function licenseDaysRemaining(license: LicenseData | null | undefined, now: Date = new Date()): number | null {
  const end = parseLicenseDate(license?.endDate);
  if (!end) return null;
  return Math.ceil((end.getTime() - now.getTime()) / 86_400_000);
}

/** Summarizes a license object into display-ready info. */
export function getLicenseInfo(license: LicenseData | null | undefined, now: Date = new Date()): LicenseInfo {
  const type = license?.type?.toLowerCase() ?? null;
  const hasLicense = !!type;
  const isTrial = type === 'trial';
  const daysRemaining = hasLicense ? licenseDaysRemaining(license, now) : null;
  const seats = typeof license?.seats === 'number' ? license.seats : null;
  const startDate = parseLicenseDate(license?.startDate);
  const endDate = parseLicenseDate(license?.endDate);
  const validityLabel = !endDate ? '' : startDate
    ? `${startDate.toLocaleDateString()} – ${endDate.toLocaleDateString()}`
    : `until ${endDate.toLocaleDateString()}`;

  let label: string;
  if (!hasLicense) {
    label = 'No license';
  } else {
    const typeLabel = type!.charAt(0).toUpperCase() + type!.slice(1);
    label = isTrial && daysRemaining != null
      ? `${typeLabel} – ${Math.max(daysRemaining, 0)} day${Math.abs(daysRemaining) === 1 ? '' : 's'} left`
      : typeLabel;
  }

  return {
    hasLicense,
    type,
    isTrial,
    daysRemaining,
    seats,
    startDate,
    endDate,
    validityLabel,
    label,
    dictKey: hasLicense ? `license.${type}` : 'license.none',
    isWarning: !hasLicense,
  };
}
